import React, { useState, useEffect, FC } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { Text, Switch, Button, IconButton, Divider, Surface, Snackbar } from 'react-native-paper';
import { TimePickerModal } from 'react-native-paper-dates';
import { getScheduleAction, updateSchedulesAction } from "../../controller/usersController.ts";
import { useAppDispatch, useAppSelector } from '../../store/hooks.ts';
import GlobalStyles from "../../resources/Styles.ts";

const RangoHoraItem = ({ diaIndex, rango, onUpdateHora, onEliminar, canDelete, errorMessages }:
  { diaIndex: number, rango: RangoHorario, onEliminar: () => void, isWeb: boolean, canDelete: boolean, onUpdateHora: Function, errorMessages: string }) => {
  const [visibleInicio, setVisibleInicio] = React.useState(false);
  const [visibleFin, setVisibleFin] = React.useState(false);

  // Callbacks para confirmar la hora
  const onConfirmInicio = React.useCallback(({ hours, minutes }) => {
    setVisibleInicio(false);
    onUpdateHora(diaIndex, rango.id, 'start_time', hours, minutes);
  }, [diaIndex, rango.id]);

  const onConfirmFin = React.useCallback(({ hours, minutes }) => {
    setVisibleFin(false);
    onUpdateHora(diaIndex, rango.id, 'end_time', hours, minutes);
  }, [diaIndex, rango.id]);

  return (
    <View style={{ marginBottom: 15 }}>
      {/* 🔴 MENSAJE DE ERROR ARRIBA */}
      {errorMessages && (
        <Text style={{ color: 'red', fontSize: 11, fontWeight: 'bold', marginBottom: 4 }}>
          {errorMessages}
        </Text>
      )}

      <View style={styles.rangoContainer}>
        {/* Botón Hora Inicio */}
        <TouchableOpacity onPress={() => setVisibleInicio(true)}>
          <Surface style={[styles.timePickerSurface, errorMessages && { borderColor: 'red', borderWidth: 1.5 }]} elevation={1}>
            <Text style={styles.timeText}>{rango.start_time}</Text>
          </Surface>
        </TouchableOpacity>

        <Text style={styles.separatorText}>-</Text>

        {/* Botón Hora Fin */}
        <TouchableOpacity onPress={() => setVisibleFin(true)}>
          <Surface style={[styles.timePickerSurface, errorMessages && { borderColor: 'red', borderWidth: 1.5 }]} elevation={1}>
            <Text style={styles.timeText}>{rango.end_time}</Text>
          </Surface>
        </TouchableOpacity>

        {/* Modales de Selección */}
        <TimePickerModal
          visible={visibleInicio}
          onDismiss={() => setVisibleInicio(false)}
          onConfirm={onConfirmInicio}
          label="Selecciona hora de inicio"
          uppercase={false}
          cancelLabel="Cancelar"
          confirmLabel="Ok"
          animationType="fade"
          locale="es" // O el que prefieras
        />

        <TimePickerModal
          visible={visibleFin}
          onDismiss={() => setVisibleFin(false)}
          onConfirm={onConfirmFin}
          label="Selecciona hora de fin"
          uppercase={false}
          cancelLabel="Cancelar"
          confirmLabel="Ok"
          animationType="fade"
          locale="es"
        />

        {canDelete && (
          <TouchableOpacity onPress={onEliminar} style={styles.eliminarButton}>
            <Text style={styles.eliminarText}>× Eliminar</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const isWeb = Platform.OS === 'web';

// --- Tus Colores Definidos Anteriormente ---
const Colors = {
  primary: '#5553F5',    // Violeta vibrante
  bgBlue: '#000336',     // Azul oscuro (Header)
  bgWhite: '#ffffff',    // Fondo blanco
  bgGrayMain: '#f3f3f9', // Gris claro de fondo
  textWhite: '#ffffff',
  textItemGrey: '#7e7f96', // Gris para textos secundarios
  blackColor: '#000000',
  greenButtonColor: '#69e4b2', // Verde para 'Disponible'
};

// --- Interfaces de Datos ---
interface RangoHorario {
  id: number; // ID único para React keys
  day_of_week: number;
  start_time: string; // Ej: "09:00 a.m." 
  end_time: string;    // Ej: "01:00 p.m."
}

interface DiaHorario {
  id: number,
  nombre: string;
  disponible: boolean;
  rangos: RangoHorario[];
}


// --- Pantalla Principal de Horario ---
export const MyScheduleScreen: FC<IRoute> = ({ route, navigation }) => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector(state => state.auth);
  const user = useAppSelector(state => state.user);

  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
  const [horarioData, setHorarioData] = useState<DiaHorario[]>([
    { id: 1, nombre: 'Lunes', disponible: false, rangos: [] },
    { id: 2, nombre: 'Martes', disponible: false, rangos: [] },
    { id: 3, nombre: 'Miércoles', disponible: false, rangos: [] },
    { id: 4, nombre: 'Jueves', disponible: false, rangos: [] },
    { id: 5, nombre: 'Viernes', disponible: false, rangos: [] },
    { id: 6, nombre: 'Sábado', disponible: false, rangos: [] },
    { id: 7, nombre: 'Domingo', disponible: false, rangos: [] }, // Indisponible
  ]);

  // --- Cargar datos de la API (Comentado para integración futura) ---
  useEffect(() => {
    loadSchedule();
  }, []);

  useEffect(() => {
    if (user.schedules) {
      loadScheduleFormat(user.schedules)
    }
  }, [user.schedules]);

  const loadSchedule = () => {
    dispatch(getScheduleAction(auth.userInfo.id));
  }

  const loadScheduleFormat = (schedules) => {
    // 1. Primero, creamos una copia limpia del estado actual para trabajar
    setHorarioData(currentData => {
      let newData = [...currentData];

      schedules.forEach((data) => {
        // 2. BUSCAMOS el día exacto en nuestro arreglo local (usando el ID del día)
        const dayIndex = newData.findIndex(d => d.id === data.day_of_week);

        if (dayIndex !== -1) {
          const hStart = data.start_time.split(":");
          const hEnd = data.end_time.split(":");

          // 3. Actualizamos ese día específico
          newData[dayIndex] = {
            ...newData[dayIndex],
            disponible: true,
            rangos: [
              ...newData[dayIndex].rangos,
              {
                id: Math.random().toString(36).substr(2, 9), // ID único para el rango
                day_of_week: data.day_of_week,
                start_time: formatHours(hStart[0], hStart[1]),
                end_time: formatHours(hEnd[0], hEnd[1])
              }
            ]
          };
        }
      });

      return newData;
    });
  };

  // --- Handlers de Interacción ---
  const toggleDia = (index: number) => {
    setHorarioData(prev => prev.map((dia, i) =>
      i === index ? { ...dia, disponible: !dia.disponible, rangos: !dia.disponible ? [{ id: Date.now(), day_of_week: dia.id, start_time: '09:00 a.m.', end_time: '06:00 p.m.' }] : [] } : dia
    ));

    // 🟢 Limpiar errores de ese día específico
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`${index}-`)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const anadirRango = (index: number) => {
    setHorarioData(prev => prev.map((dia, i) =>
      i === index ? { ...dia, rangos: [...dia.rangos, { id: Date.now(), day_of_week: dia.id, start_time: '04:00 p.m.', end_time: '08:00 p.m.' }] } : dia
    ));
  };

  const eliminarRango = (diaIndex: number, rangoId: number) => {
    setHorarioData(prev => prev.map((dia, i) =>
      i === diaIndex ? { ...dia, rangos: dia.rangos.filter(r => r.id !== rangoId) } : dia
    ));

    // 🟢 Limpiamos el error asociado a ese rango eliminado
    setErrors(prev => {
      const newErros = { ...prev };
      delete newErros[`${diaIndex}-${rangoId}`];
      return newErros;
    });
  };

  const getMessageError = (inicio, fin, diaIndex, rangoIdActual) => {
    const inicioNum = aMinutos(inicio);
    const finNum = aMinutos(fin);
    const dia = horarioData[diaIndex];

    // Regla 1: Fin después de Inicio
    if (finNum <= inicioNum) {
      return "La hora de fin debe ser posterior a la de inicio";
    }

    // Regla 2: Solapamiento
    const seSolapa = dia.rangos.some(r => {
      if (r.id === rangoIdActual) return false;
      const exIn = aMinutos(r.start_time);
      const exOut = aMinutos(r.end_time);
      // Lógica de cruce: (NuevoInicio < ViejoFin) Y (NuevoFin > ViejoInicio)
      return (inicioNum < exOut && finNum > exIn);
    });

    if (seSolapa) return "Este horario se cruza con otro del mismo día";

    return null;
  };

  const handleGuardar = () => {
    let foundErrors = {};
    let hasErrors = false;

    // 1. Validar cada día y cada rango activo
    horarioData.forEach((dia, diaIndex) => {
      if (dia.disponible) {
        dia.rangos.forEach((rango) => {
          // Ejecutamos la lógica de validación para cada rango existente
          const msg = getMessageError(rango.start_time, rango.end_time, diaIndex, rango.id);

          if (msg) {
            foundErrors[`${diaIndex}-${rango.id}`] = msg;
            hasErrors = true;
          }
        });
      }
    });

    // 2. Si detectamos problemas, actualizamos el estado visual y detenemos el guardado
    if (hasErrors) {
      setErrors(foundErrors);
      return; // 🛑 Bloquea el envío a la API
    }

    setErrors({});

    // 3. Si todo está limpio, procedemos
    const saveData = prepareScheduleForSaving();
    dispatch(updateSchedulesAction(auth.userInfo.id, saveData));

    // 🟢 Mostramos el mensaje de éxito
    setSnackbarVisible(true);
  };

  const handleUpdateHora = (diaIndex, rangoId, campo, hours, minutes) => {
    let formattedTime = formatHours(hours, minutes);

    setHorarioData(prev => {
      const nuevosDatos = prev.map((dia, i) => {
        if (i === diaIndex) {
          return {
            ...dia,
            rangos: dia.rangos.map(r => {
              if (r.id === rangoId) {
                const nuevoRango = { ...r, [campo]: formattedTime };
                // 🟢 VALIDAMOS AQUÍ:
                validarHorario(diaIndex, rangoId, nuevoRango.start_time, nuevoRango.end_time);
                return nuevoRango;
              }
              return r;
            })
          };
        }
        return dia;
      });
      return nuevosDatos;
    });
  };

  const formatHours = (hours, minutes) => {
    const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
    const h12 = hours % 12 || 12;
    const formattedTime = `${String(h12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
    return formattedTime;
  }

  const prepareScheduleForSaving = () => {
    const result = [];

    horarioData.forEach((dia) => {
      // Solo procesamos los días que están marcados como disponibles
      if (dia.disponible && dia.rangos.length > 0) {
        dia.rangos.forEach((rango) => {
          result.push({
            day_of_week: dia.id, // O el ID numérico del día (0-6)
            start_time: convertTo24H(rango.start_time),
            end_time: convertTo24H(rango.end_time)
          });
        });
      }
    });

    return result;
  };

  // Función auxiliar para convertir "12:20 PM" -> "12:20:00" o "03:00 PM" -> "15:00:00"
  const convertTo24H = (timeStr) => {
    if (!timeStr) return "00:00:00";

    // 1. Limpiamos el string y separamos la parte AM/PM
    // Esto maneja tanto "p.m." como "PM" o "p. m."
    const lowerTime = timeStr.toLowerCase().replace(/\./g, '').trim();
    const isPM = lowerTime.includes('pm');
    const isAM = lowerTime.includes('am');

    // 2. Extraemos solo los números (ej. "03:00")
    let [hours, minutes] = lowerTime.replace(/[ap]m/, '').trim().split(':');

    let h = parseInt(hours, 10);

    // 3. Lógica de conversión Real
    if (isPM && h < 12) {
      h += 12; // "03:00 PM" -> 15
    } else if (isAM && h === 12) {
      h = 0;   // "12:00 AM" -> 00
    }
    // Si es 12 PM, se queda como 12.

    // 4. Formateo con ceros a la izquierda
    const finalHours = String(h).padStart(2, '0');
    const finalMinutes = String(minutes || '00').padStart(2, '0');

    return `${finalHours}:${finalMinutes}:00`;
  };

  const aMinutos = (horaStr: string) => {
    if (!horaStr) return 0;
    // Limpiamos "09:00 a.m." -> "09:00"
    const [horaPart, ampm] = horaStr.toLowerCase().split(' ');
    let [horas, minutos] = horaPart.split(':').map(Number);

    if (ampm === 'p.m.' && horas < 12) horas += 12;
    if (ampm === 'a.m.' && horas === 12) horas = 0;

    return horas * 60 + minutos;
  };

  const validarHorario = (diaIndex: number, rangoId: string | number, inicio: string, fin: string) => {
    const inicioNum = aMinutos(inicio);
    const finNum = aMinutos(fin);
    const dia = horarioData[diaIndex];

    let mensaje = null;

    if (finNum <= inicioNum) {
      mensaje = "La hora de fin debe ser posterior a la de inicio";
    } else {
      // Validar solapamiento con otros rangos del mismo día
      const seSolapa = dia.rangos.some(r => {
        if (r.id === rangoId) return false;
        const exIn = aMinutos(r.start_time);
        const exOut = aMinutos(r.end_time);
        return (inicioNum < exOut && finNum > exIn);
      });
      if (seSolapa) mensaje = "Este horario se cruza con otro del mismo día";
    }

    // Guardamos el error usando una llave única: "dia-idRango"
    setErrors(prev => ({ ...prev, [`${diaIndex}-${rangoId}`]: mensaje }));
  };

  // --- Renderizado de Fila de Día ---
  const renderDiaRow = (dia: DiaHorario, index: number) => {
    //console.log("render", dia, index)
    return (
      <View key={dia.nombre} style={styles.diaRowContainer}>
        <View style={styles.diaInfoCol}>
          <Text style={styles.diaNameText}>{dia.nombre}</Text>
          <View style={styles.statusBadgeContainer}>
            <View style={[styles.statusDot, { backgroundColor: dia.disponible ? Colors.greenButtonColor : Colors.textItemGrey }]} />
            <Text style={[styles.statusText, { color: dia.disponible ? Colors.greenButtonColor : Colors.textItemGrey }]}>
              {dia.disponible ? 'Disponible' : 'Indisponible'}
            </Text>
          </View>
        </View>

        <View style={styles.switchCol}>
          <Switch
            value={dia.disponible}
            onValueChange={() => toggleDia(index)}
            color={Colors.primary}
          />
        </View>

        <View style={styles.rangosCol}>
          {dia.disponible ? (
            <View>
              {dia.rangos.map(rango => (
                <RangoHoraItem
                  key={rango.id}
                  diaIndex={index}
                  rango={rango}
                  onEliminar={() => eliminarRango(index, rango.id)}
                  isWeb={isWeb}
                  canDelete={dia.rangos.length > 1}
                  onUpdateHora={handleUpdateHora}
                  errorMessages={errors[`${index}-${rango.id}`]}
                />
              ))}
              <TouchableOpacity onPress={() => {
                //handleAnadirHorario(index)
                anadirRango(index)
              }} style={styles.anadirRangoButton}>
                <Text style={styles.anadirRangoText}>+ Añadir Horario</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <Text style={styles.indisponiblePlaceholder}>Indisponible</Text>
          )}
        </View>
      </View>
    )
  };

  return (
    <View style={styles.mainContainer}>

      {/* --- Contenido Principal de Horario --- */}
      <ScrollView contentContainerStyle={styles.scrollContent} style={styles.scrollBody}>
        <Surface style={styles.contentSurface} elevation={1}>
          <View style={styles.paddingContainer}>

            {/* BLOQUE 1: Título solo, arriba de todo */}
            <Text style={styles.mainTitle}>Mi Horario de Disponibilidad</Text>

            {/* BLOQUE 2: Fila para texto y botón */}
            <View style={styles.contentRow}>

              {/* Columna Izquierda: El texto que debe doblarse */}
              <View style={styles.textWrapper}>
                <Text style={styles.mainSubtitle}>
                  Configura tus días y horas de disponibilidad para que las conversaciones solo se te asignen cuando estés disponible.
                </Text>
              </View>

              {/* Columna Derecha: El botón que debe quedarse quieto */}
              <View style={styles.buttonWrapper}>
                <Button
                  mode="contained"
                  onPress={handleGuardar}
                  labelStyle={styles.guardarButtonLabel}
                  style={[
                    styles.guardarButton,
                  ]}
                >
                  Guardar Cambios
                </Button>
              </View>

            </View>
          </View>

          <Divider style={styles.mainDivider} />

          {/* Renderizado de la lista de días */}
          {horarioData.map(renderDiaRow)}
        </Surface>
      </ScrollView>
      {/* 🟢 Popup de Éxito */}
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={2500}
        // 🟢 Esto lo posiciona arriba (en lugar de abajo por defecto)
        wrapperStyle={{ top: 50 }}
        style={styles.snackbarUp}
        action={{
          label: 'OK',
          onPress: () => setSnackbarVisible(false),
        }}
      >
        Cambios guardados con éxito
      </Snackbar>
    </View>
  );
};

// --- Estilos de la Pantalla ---
const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: Colors.bgGrayMain,
    ...Platform.select({ web: { height: '100vh' } }), // Asegura altura completa en web
  },

  contentSurface: {
    margin: 16,
    width: '100%',
    maxWidth: 1000, // Ancho máximo para que no se vea gigante en monitores grandes
    backgroundColor: Colors.bgWhite,
    borderRadius: 12,
    padding: isWeb ? 30 : 20,
  },
  paddingContainer: {
    padding: 20, // Este es el margen interno del cuadro blanco
  },
  mainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 10,
    width: '100%',
  },
  contentRow: {
    flexDirection: 'row',        // Los pone en la misma línea
    alignItems: 'flex-start',    // Alinea el botón con la primera línea del texto
    justifyContent: 'space-between',
    width: '100%',               // 🟢 Obligatorio para no salirse
  },
  textWrapper: {
    flex: 1,                     // 🟢 MAGIA: Dice "usa solo el espacio que te dejen"
    paddingRight: 20,            // Espacio para que no choquen
  },
  buttonWrapper: {
    flexShrink: 0,               // 🟢 Evita que el botón se aplaste
  },
  mainSubtitle: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  guardarButton: {
    backgroundColor: '#5A56E0',
    borderRadius: 8,
  },
  guardarButtonLabel: {
    fontSize: 13,
    fontWeight: 'bold',
  },


  headerMainContainer: {
    padding: 20,
    width: '100%', // Asegura que el contenedor no mida más que el Surface
  },

  textContainer: {
    flex: 1,                  // 🟢 CRÍTICO: Obliga al texto a tomar solo el espacio que sobra
    paddingRight: 15,         // Espacio de seguridad entre texto y botón
  },
  buttonContainer: {
    flexShrink: 0,            // 🟢 CRÍTICO: Impide que el botón se haga chiquito o se corte
  },

  tabsContainer: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
    height: 50,
  },
  tabItem: {
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  tabItemActive: {
    // backgroundColor: 'rgba(255,255,255,0.1)',
  },
  tabText: {
    color: Colors.textWhite,
    fontSize: 14,
    fontWeight: '500',
    opacity: 0.7,
  },
  tabTextActive: {
    opacity: 1,
    fontWeight: '600',
  },
  activeTabIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 20,
    right: 20,
    height: 3,
    backgroundColor: Colors.bgWhite,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  },

  // Contenido Principal
  scrollBody: {
    flex: 1,
  },
  scrollContent: {
    padding: isWeb ? 40 : 15,
    alignItems: 'center',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 20,
  },

  mainDivider: {
    marginBottom: 25,
    backgroundColor: '#e1e1e8',
  },

  // Fila de Día
  diaRowContainer: {
    flexDirection: 'row',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f5',
    alignItems: 'flex-start',
  },
  diaInfoCol: {
    width: isWeb ? 150 : 100,
    paddingRight: 10,
  },
  diaNameText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.blackColor,
    marginBottom: 5,
  },
  statusBadgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
  },
  switchCol: {
    width: 80,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 5,
  },
  rangosCol: {
    flex: 1,
    paddingLeft: isWeb ? 20 : 10,
  },

  // Rangos de Hora
  rangoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    flexWrap: 'wrap', // Para móviles
  },
  timePickerSurface: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e1e8',
    backgroundColor: '#fafafc',
    minWidth: 110,
    alignItems: 'center',
  },
  timeText: {
    fontSize: 14,
    color: Colors.blackColor,
    fontWeight: '500',
  },
  separatorText: {
    fontSize: 18,
    color: Colors.textItemGrey,
    marginHorizontal: 12,
  },
  eliminarButton: {
    marginLeft: 15,
    paddingVertical: 5,
  },
  eliminarText: {
    color: '#E0434C', // Color Amaranth de tu paleta de chat para errores/eliminar
    fontSize: 13,
    fontWeight: '500',
  },
  anadirRangoButton: {
    marginTop: 5,
    paddingVertical: 5,
  },
  anadirRangoText: {
    color: Colors.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  indisponiblePlaceholder: {
    fontSize: 14,
    color: Colors.textItemGrey,
    fontStyle: 'italic',
    paddingTop: 10,
  },
  snackbarUp: {
    backgroundColor: Colors.primary,
    borderRadius: 25, // Un poco más redondeado se ve más "popup"
    alignSelf: 'center', // 🟢 Evita que ocupe todo el ancho
    width: isWeb ? 400 : '85%', // 🟢 Ancho controlado
    maxWidth: 500,
  },
  ...GlobalStyles,
});